import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import React, { ReactNode } from 'react'
import { keyframes, styled } from '../../lib/stitches'
import { gray14, gray2 } from '../platform/color'

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
})

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' }
})

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
})

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' }
})

const StyledContent = styled(TooltipPrimitive.Content, {
  zIndex: '500',
  fontFamily: 'DM Sans',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: '12px',
  lineHeight: '14px',
  color: gray2,
  background: gray14,
  boxShadow: 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '400ms',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'forwards',
    willChange: 'transform, opacity',
    '&[data-state="delayed-open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade }
    }
  }
})

const StyledArrow = styled(TooltipPrimitive.Arrow, {
  fill: gray14
})

// Exports
export const Provider = TooltipPrimitive.Provider
export const Tooltip = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger
export const TooltipContent = StyledContent

function withTooltip<P extends object>(
  baseComponent: React.FunctionComponent<P>,
  side?: 'bottom' | 'left' | 'right' | 'top',
  isDisabled?: boolean
): React.FunctionComponent<P & { tooltipText?: ReactNode; isDisabled?: boolean }> {
  const Component = baseComponent
  return (props: P & { tooltipText?: string; isDisabled?: boolean }) => {
    const { tooltipText, isDisabled, ...otherProps } = props

    if (isDisabled) {
      return <Component {...(otherProps as P)} />
    }

    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Component {...(otherProps as P)} />
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <StyledContent sideOffset={5} side={side}>
            {tooltipText}
            <StyledArrow />
          </StyledContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    )
  }
}

export default withTooltip
